import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowLeft, ArrowRight, PackageX, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const Cart = () => {
  const navigate = useNavigate();
  const { 
    cart, 
    removeFromCart, 
    updateQuantity, 
    getCartTotal, 
    getCartCount,
    getTaxAmount,
    getGrandTotal,
    clearCart 
  } = useCart();

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) {
      return;
    }
    updateQuantity(productId, newQuantity);
  };

  const handleRemoveItem = (productId, productName) => {
    removeFromCart(productId);
    toast.success(`${productName} wurde aus dem Warenkorb entfernt`);
  };

  const handleClearCart = () => {
    clearCart();
    toast.success('Warenkorb wurde geleert');
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Ihr Warenkorb ist leer');
      return;
    }
    // Navigate to checkout (to be implemented)
    navigate('/checkout');
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // Empty cart state
  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="py-12 md:py-20 bg-gray-50">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Mobile Überschrift - über dem Bild */}
            <div className="text-center mb-6 md:hidden">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Warenkorb</h1>
              <p className="text-base text-gray-600">Ihr Einkaufswagen ist leer</p>
            </div>
            
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://customer-assets.emergentagent.com/job_7062bdfd-4f57-4d85-897a-fa745eecbadc/artifacts/x0bekbcf_9F5B6D75-A522-48E0-A711-8205BB478F4D.png"
                alt="Warenkorb - Jannesmann Baumaschinen"
                className="w-full h-[280px] sm:h-[400px] md:h-[860px] object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent hidden md:flex items-end">
                <div className="px-12 pb-10">
                  <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Warenkorb</h1>
                  <p className="text-lg text-gray-200">Ihr Einkaufswagen ist leer</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <Card className="max-w-lg mx-auto text-center p-8">
            <PackageX className="mx-auto text-gray-400 mb-6" size={80} />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ihr Warenkorb ist leer</h2>
            <p className="text-gray-600 mb-8">
              Entdecken Sie unsere hochwertigen Baumaschinen und fügen Sie Produkte hinzu.
            </p>
            <Link to="/maschinen">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white">
                <ArrowLeft className="mr-2" size={18} />
                Zu den Maschinen
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Überschrift - über dem Bild */}
          <div className="text-center mb-6 md:hidden">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Warenkorb</h1>
            <p className="text-base text-gray-600">{getCartCount()} Artikel in Ihrem Warenkorb</p>
          </div>
          
          <div className="relative rounded-2xl overflow-hidden">
            <img
              src="https://customer-assets.emergentagent.com/job_7062bdfd-4f57-4d85-897a-fa745eecbadc/artifacts/x0bekbcf_9F5B6D75-A522-48E0-A711-8205BB478F4D.png"
              alt="Warenkorb - Jannesmann Baumaschinen"
              className="w-full h-[280px] sm:h-[400px] md:h-[860px] object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/20 to-transparent hidden md:flex items-end">
              <div className="px-12 pb-10">
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Warenkorb</h1>
                <p className="text-lg text-gray-200">{getCartCount()} Artikel in Ihrem Warenkorb</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back to Shopping */}
        <Link to="/maschinen" className="inline-flex items-center text-gray-600 hover:text-red-600 mb-6 transition-colors">
          <ArrowLeft className="mr-2" size={18} />
          Weiter einkaufen
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items - 2 columns on large screens */}
          <div className="lg:col-span-2 space-y-4">
            {cart.map((item) => {
              const itemPrice = item.pricing?.numericPrice || 0;
              const itemTotal = itemPrice * item.quantity;

              return (
                <Card key={item.id} className="overflow-hidden" data-testid={`cart-item-${item.id}`}>
                  <CardContent className="p-0">
                    <div className="flex flex-col sm:flex-row">
                      {/* Product Image */}
                      <Link 
                        to={`/maschinen/${item.slug}`}
                        className="w-full sm:w-40 h-40 bg-gray-100 flex-shrink-0"
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-contain p-2 hover:scale-105 transition-transform"
                        />
                      </Link>

                      {/* Product Details */}
                      <div className="flex-1 p-4 flex flex-col justify-between">
                        <div>
                          <Link 
                            to={`/maschinen/${item.slug}`}
                            className="text-lg font-bold text-gray-900 hover:text-red-600 transition-colors"
                            data-testid={`cart-item-name-${item.id}`}
                          >
                            {item.name}
                          </Link>
                          {item.model && (
                            <p className="text-sm text-gray-500">Modell: {item.model}</p>
                          )}
                          <p className="text-red-600 font-semibold mt-1" data-testid={`cart-item-price-${item.id}`}>
                            {item.pricing?.buy || 'Auf Anfrage'}
                          </p>
                        </div>

                        {/* Quantity Controls & Remove - Mobile Optimized */}
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 gap-4">
                          {/* Quantity Controls */}
                          <div className="flex items-center">
                            <span className="text-sm text-gray-600 mr-3">Menge:</span>
                            <div className="flex items-center border rounded-lg overflow-hidden">
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                                className="p-2 hover:bg-gray-100 transition-colors disabled:opacity-50"
                                disabled={item.quantity <= 1}
                                data-testid={`cart-decrease-${item.id}`}
                              >
                                <Minus size={16} />
                              </button>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity}
                                onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value) || 1)}
                                className="w-14 text-center border-x py-2 focus:outline-none"
                                data-testid={`cart-quantity-${item.id}`}
                              />
                              <button
                                onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                                className="p-2 hover:bg-gray-100 transition-colors"
                                data-testid={`cart-increase-${item.id}`}
                              >
                                <Plus size={16} />
                              </button>
                            </div>
                          </div>

                          {/* Item Total & Remove */}
                          <div className="flex items-center justify-between sm:justify-end gap-4">
                            <span className="font-bold text-gray-900" data-testid={`cart-item-total-${item.id}`}>
                              {itemPrice > 0 ? formatPrice(itemTotal) : 'Auf Anfrage'}
                            </span>
                            <button
                              onClick={() => handleRemoveItem(item.id, item.name)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                              title="Entfernen"
                              data-testid={`cart-remove-${item.id}`}
                            >
                              <Trash2 size={20} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Clear Cart Button */}
            <div className="flex justify-end">
              <Button 
                variant="outline" 
                onClick={handleClearCart}
                className="text-gray-600 hover:text-red-600 hover:border-red-600"
                data-testid="clear-cart-btn"
              >
                <Trash2 className="mr-2" size={16} />
                Warenkorb leeren
              </Button>
            </div>
          </div>

          {/* Order Summary - Sticky on large screens */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24 shadow-xl border-2 border-gray-200">
              <CardHeader className="bg-gray-50 border-b">
                <CardTitle className="flex items-center text-xl">
                  <ShoppingCart className="mr-2 text-red-600" size={24} />
                  Bestellübersicht
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {/* Subtotal */}
                <div className="flex justify-between text-gray-700">
                  <span>Zwischensumme (netto)</span>
                  <span data-testid="cart-subtotal">{formatPrice(getCartTotal())}</span>
                </div>

                {/* Tax */}
                <div className="flex justify-between text-gray-700">
                  <span>MwSt. (19%)</span>
                  <span data-testid="cart-tax">{formatPrice(getTaxAmount())}</span>
                </div>

                {/* Shipping Notice */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start">
                  <Truck className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" size={18} />
                  <p className="text-sm text-yellow-800">
                    Versandkosten werden separat berechnet (auf Anfrage)
                  </p>
                </div>

                {/* Divider */}
                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg font-bold text-gray-900">
                    <span>Gesamtsumme</span>
                    <span className="text-red-600" data-testid="cart-total">{formatPrice(getGrandTotal())}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">zzgl. 19% MwSt.</p>
                </div>

                {/* Checkout Button */}
                <Button 
                  onClick={handleCheckout}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-6 text-lg mt-4"
                  data-testid="checkout-btn"
                >
                  Zur Kasse
                  <ArrowRight className="ml-2" size={20} />
                </Button>

                {/* Trust Elements */}
                <div className="pt-4 border-t space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <span className="text-green-600 mr-2">✓</span>
                    Sichere Zahlung mit Stripe
                  </div>
                  <div className="flex items-center">
                    <span className="text-green-600 mr-2">✓</span>
                    CE-zertifizierte Maschinen
                  </div>
                  <div className="flex items-center">
                    <span className="text-green-600 mr-2">✓</span>
                    Professionelle Beratung
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-12">
          <Card className="bg-gradient-to-br from-gray-900 to-gray-800 text-white">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Haben Sie Fragen zu Ihrer Bestellung?</h3>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                Unser Team berät Sie gerne persönlich zu Produkten, Versand und Zahlungsoptionen.
              </p>
              <Link to="/kontakt">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-gray-900">
                  Kontakt aufnehmen
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Cart;
